import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { HomeCategorySection } from "@/components/storefront/HomeCategorySection";
import { PlayfulError, PlayfulNotFound } from "@/components/storefront/PlayfulError";
import { listCategories, listCategoryProductCounts } from "@/lib/products.functions";
import { BACK_TO_SCHOOL_CATEGORY_IDS } from "@/lib/back-to-school";
import { buildCategoryIconMap } from "@/lib/category-icon";
import { useMemo } from "react";

const TITLE = "Back to School 2026 — Σχολικά είδη | MrPanda.gr";
const DESCRIPTION =
  "Όλα τα σχολικά σε ένα μέρος: τετράδια, μολύβια, κασετίνες, τσάντες, χρώματα και είδη ζωγραφικής σε τιμές MrPanda, με γρήγορη αποστολή σε όλη την Ελλάδα.";

export const Route = createFileRoute("/back-to-school")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mrpanda.gr/back-to-school" },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/back-to-school" }],
  }),
  loader: async ({ context }: any) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["categories"],
      queryFn: () => listCategories(),
      staleTime: 5 * 60_000,
    });
    return null;
  },
  component: BackToSchoolPage,
  errorComponent: ({ error, reset }) => (
    <StorefrontLayout>
      <PlayfulError error={error instanceof Error ? error : new Error(String(error))} reset={reset} />
    </StorefrontLayout>
  ),
  notFoundComponent: () => (
    <StorefrontLayout>
      <PlayfulNotFound />
    </StorefrontLayout>
  ),
});

type Cat = { ps_id: number; name: string; parent_ps_id: number | null };

function BackToSchoolPage() {
  const { data: catData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => listCategories(),
    staleTime: 5 * 60_000,
  });
  const categories = (catData?.categories ?? []) as Cat[];
  useMemo(() => {
    if (categories.length > 0) buildCategoryIconMap(categories as never);
  }, [categories]);

  const { data: countsData } = useQuery({
    queryKey: ["bts", "counts"],
    queryFn: () => listCategoryProductCounts({ data: { categoryPsIds: [...BACK_TO_SCHOOL_CATEGORY_IDS] } }),
    staleTime: 30 * 60_000,
  });

  const sections = useMemo(() => {
    const byPsId = new Map(categories.map((c) => [c.ps_id, c]));
    const counts = new Map((countsData?.counts ?? []).map((c) => [c.ps_id, c.count]));
    return BACK_TO_SCHOOL_CATEGORY_IDS
      .flatMap((id) => {
        const cat = byPsId.get(id);
        const count = counts.get(id) ?? 0;
        return cat && count > 0 ? [{ ps_id: id, name: cat.name, count }] : [];
      })
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "el"));
  }, [categories, countsData]);


  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-10 pt-6">
        {/* Ήρωας — μαυροπίνακας με κιμωλία */}
        <section className="bts-chalkboard relative mb-6 overflow-hidden rounded-3xl px-6 py-10 text-center shadow-lg sm:py-14">
          {["✏️", "📚", "🎒", "📐", "🖍️", "🍎"].map((d, i) => (
            <span
              key={i}
              aria-hidden
              className="pointer-events-none absolute select-none text-4xl opacity-25 animate-float-y sm:text-5xl"
              style={{
                top: `${8 + ((i * 37) % 70)}%`,
                left: `${(i * 19 + 4) % 92}%`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              {d}
            </span>
          ))}
          <div className="relative">
            <span className="bts-sticky inline-block rounded-lg px-3 py-1 text-xs font-extrabold">
              Back to School 🎒
            </span>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl">
              Έτοιμοι για σχολείο με τον MrPanda! 🐼
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-base font-semibold opacity-90 sm:text-lg">
              Τετράδια, κασετίνες, μολύβια, χρώματα, τσάντες &amp; ό,τι χρειάζεσαι για την νέα σχολική
              χρονιά — όλα σε τιμές MrPanda.
            </p>
          </div>
        </section>

        {/* Ενότητες ανά κατηγορία (όπως στην αρχική), με τις μεγαλύτερες πρώτα */}
        {sections.map((c) => (
          <HomeCategorySection
            key={c.ps_id}
            psId={c.ps_id}
            name={c.name}
            descendantIds={[c.ps_id]}
            limit={12}
          />
        ))}
      </div>
    </StorefrontLayout>
  );
}
